/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import {
  getContentAlbumImage,
  getContentDescription,
  getContentHasVideo,
  getContentMetadata,
  getContentTitle,
  getShareProperties,
} from "@packages/media";
import type { QueueManagedItem } from "@packages/sdk";
import {
  useBranch,
  useRedirectToAuth,
  useRequestFavorite,
  useRequestPrayer,
  useSession,
} from "@packages/sdk";
import type { HallowElementProps } from "@packages/ui";
import {
  colors,
  ContentMoreMenuHeader,
  DropdownMenu,
  DropdownMenuItem,
  HideFromKids,
  IconButton,
  MoreIcon,
  ShareIcon,
  ShareModal,
  spacing,
  StarIcon,
  ThemeProvider,
  useModal,
  zIndices,
} from "@packages/ui";
import { useContext } from "react";

import { PlayerInternalContext } from "../../core/MediaPlayer.emotion";

const dropdownMenuContentStyle: Interpolation = {
  paddingTop: spacing.l,
  maxWidth: "min(400px, 100dvw)",
};
const modalZStyle: Interpolation = {
  zIndex: zIndices.modalOverQueue,
};
const noBackgroundStyle: Interpolation = {
  background: colors.transparent,
};
const modalPaddingStyle: Interpolation = {
  paddingTop: spacing.l,
};

export type MediaMoreMenuProps = Omit<HallowElementProps<"div">, "content"> & {
  content: QueueManagedItem;
  onOpenChange?: (open: boolean) => void;
};

export const MediaMoreMenu = ({
  content,
  onOpenChange,
  ...props
}: MediaMoreMenuProps) => {
  const { mutationPost: updateFavorite } = useRequestFavorite({
    id: content?.type === "prayer" ? content.prayer.id : null,
  });
  const { query: prayerQuery } = useRequestPrayer({
    id: content?.type === "prayer" ? content.prayer.id : null,
  });
  const { status } = useSession();
  const shareModal = useModal(ShareModal);
  const branch = useBranch();
  const analytics = useAnalytics();
  const t = useTranslations();
  const { popupRef } = useContext(PlayerInternalContext);
  const redirectToAuth = useRedirectToAuth();

  const onClickFavorite = () => {
    if (status === "authenticated") updateFavorite.mutate();
    else redirectToAuth();
  };

  return (
    <ThemeProvider forceTheme="dark" as={null}>
      <div css={noBackgroundStyle} onClick={(e) => e.stopPropagation()}>
        <DropdownMenu
          trigger={
            <IconButton
              icon={<MoreIcon />}
              onClick={(e) => {
                // The stopImmediatePropagation call prevents next top loader from firing unnecessarily
                e.nativeEvent.stopImmediatePropagation();
                e.preventDefault();
              }}
              variant="transparentWhite"
            />
          }
          align="end"
          contentCSS={[dropdownMenuContentStyle, modalZStyle]}
          contentProps={props}
          modal={false}
          onOpenChange={onOpenChange}
          portalProps={{ container: popupRef.current }}
        >
          <ContentMoreMenuHeader
            title={getContentTitle(content)}
            subtitle={getContentDescription(content)}
            metadata={getContentMetadata(content)}
            imageSrc={getContentAlbumImage(content)}
            hasVideoContent={getContentHasVideo(content)}
          />
          {content?.type !== "radio_song" ? (
            <DropdownMenuItem
              startIcon={
                prayerQuery.data?.is_favorite ? (
                  <StarIcon.Off />
                ) : (
                  <StarIcon.Outline />
                )
              }
              onClick={onClickFavorite}
            >
              {prayerQuery.data?.is_favorite
                ? t("dialog_prayer_options_unfavorite")
                : t("dialog_prayer_options_favorite")}
            </DropdownMenuItem>
          ) : null}
          <HideFromKids>
            <DropdownMenuItem
              startIcon={<ShareIcon />}
              onClick={async () => {
                const event =
                  content?.type === "prayer"
                    ? "Tapped Share Prayer"
                    : content?.type === "radio_song"
                      ? "Tapped Share Radio Station"
                      : null;
                if (event) {
                  const props: Record<string, number> = {};
                  if (content.type === "prayer")
                    props.prayer = content.prayer.id;
                  else if (content.type === "radio_song")
                    props.radio_station = content.radio_station.id;
                  analytics.track({
                    event,
                    ...props,
                  });
                }
                const shareProps = await getShareProperties(content, branch, [
                  modalZStyle,
                  modalPaddingStyle,
                ]);
                return shareModal.show({
                  ...shareProps,
                  overlayProps: {
                    onPointerDown: () => {
                      analytics.track({
                        event: HallowAnalyticsEvent.ClosedShareAudioView,
                      });
                    },
                  },
                  portalProps: { container: popupRef.current },
                });
              }}
            >
              {t("dialog_prayer_options_share")}
            </DropdownMenuItem>
          </HideFromKids>
        </DropdownMenu>
      </div>
    </ThemeProvider>
  );
};
